import React, { useEffect, useRef, useState, useMemo } from 'react'
import { KeyDataSearch, DataSearchType, DATA_SEARCH_INIT, stayDayOptions, regionOptions, acceptClassOptions } from './utils'
import SuggestionTextSearch from './SuggestionTextSearch'
import { useTranslation } from 'react-i18next'

const IconMinus = () => {
  return (
    <svg className="h-8" fill="none" height={36} viewBox="0 0 36 36" width={36} xmlns="http://www.w3.org/2000/svg">
      <path
        clipRule="evenodd"
        d="M13 18C13 17.7348 13.1054 17.4804 13.2929 17.2929C13.4804 17.1054 13.7348 17 14 17H22C22.2652 17 22.5196 17.1054 22.7071 17.2929C22.8946 17.4804 23 17.7348 23 18C23 18.2652 22.8946 18.5196 22.7071 18.7071C22.5196 18.8946 22.2652 19 22 19H14C13.7348 19 13.4804 18.8946 13.2929 18.7071C13.1054 18.5196 13 18.2652 13 18Z"
        fill="#4B5563"
        fillRule="evenodd"
      />
      <rect height={35} rx="17.5" stroke="#E5E7EB" width={35} x="0.5" y="0.5" />
    </svg>
  )
}

const IconPlus = () => {
  return (
    <svg className="h-8" fill="none" height={40} viewBox="0 0 40 40" width={40} xmlns="http://www.w3.org/2000/svg">
      <path
        clipRule="evenodd"
        d="M20 15C20.2652 15 20.5196 15.1054 20.7071 15.2929C20.8946 15.4804 21 15.7348 21 16V19H24C24.2652 19 24.5196 19.1054 24.7071 19.2929C24.8946 19.4804 25 19.7348 25 20C25 20.2652 24.8946 20.5196 24.7071 20.7071C24.5196 20.8946 24.2652 21 24 21H21V24C21 24.2652 20.8946 24.5196 20.7071 24.7071C20.5196 24.8946 20.2652 25 20 25C19.7348 25 19.4804 24.8946 19.2929 24.7071C19.1054 24.5196 19 24.2652 19 24V21H16C15.7348 21 15.4804 20.8946 15.2929 20.7071C15.1054 20.5196 15 20.2652 15 20C15 19.7348 15.1054 19.4804 15.2929 19.2929C15.4804 19.1054 15.7348 19 16 19H19V16C19 15.7348 19.1054 15.4804 19.2929 15.2929C19.4804 15.1054 19.7348 15 20 15Z"
        fill="#4B5563"
        fillRule="evenodd"
      />
      <rect height={39} rx="19.5" stroke="#E5E7EB" width={39} x="0.5" y="0.5" />
    </svg>
  )
}

type Props = {
  open: boolean
  onClose: () => void
  initDataSearch: DataSearchType
  onChangeDataSearch: (data: DataSearchType) => void
  handleClearTopUrl: () => void
  isShowCategoryNameOptions?: boolean
}

const isMobileScreen = window.innerWidth < 768
const isTabletScreen = window.innerWidth < 1024 && window.innerWidth > 780
const isSmallMobileScreen = window.innerWidth <= 360
const currentYear = new Date().getFullYear()

export const ModalSearch: React.FC<Props> = ({ open, onClose, initDataSearch, onChangeDataSearch, handleClearTopUrl }) => {
  const [dataSearch, setDataSearch] = useState<DataSearchType>(initDataSearch)
  const [modalSearchHeight, setModalSearchHeight] = useState(0)
  const [modalSearchContentHeight, setModalSearchContentHeight] = useState(0)
  const modalSearchRef = useRef(null)
  const modalSearchContentRef = useRef(null)
  const isContentOverHeight = modalSearchContentHeight > modalSearchHeight
  const { t } = useTranslation()
  const STAY_DAY_OPTIONS = useMemo(() => stayDayOptions(t).filter((item) => Number(item.value) >= 7), [])
  const REGION_OPTIONS = useMemo(() => regionOptions(t), [])
  const ACCEPT_CLASS_OPTIONS = useMemo(() => acceptClassOptions(t), [])

  useEffect(() => {
    setModalSearchHeight(modalSearchRef?.current?.clientHeight)
    setModalSearchContentHeight(modalSearchContentRef?.current?.clientHeight)
  }, [])

  const handleRemoveChoseValueFromDataArray = (array: string[], value: string) => {
    return array.filter((item) => item !== value)
  }

  const handleAddChoseValueToDataArray = (array: string[], value: string) => {
    return [...array, value]
  }

  const handleChangeDataSearch = (key: KeyDataSearch, value: number | string, type?: 'down' | 'up' | 'option' | 'text') => {
    let newValue: number | string = value

    switch (type) {
      case 'up':
      case 'down': {
        const increment = type === 'up' ? 1 : -1
        let potentialNewValue = Number(dataSearch[key]) + increment
        newValue = Math.max(0, Math.min(20, potentialNewValue))
        const updateDataSearch = { ...dataSearch }
        if (key === 'maximum_number_of_children') {
          if (type === 'up' && dataSearch[key] < 20) {
            updateDataSearch.accept_class = [
              ...updateDataSearch.accept_class,
              { id: updateDataSearch.accept_class.length + 1, value: 'no_enrollment', name: '登園なし' },
            ]
          } else if (type === 'down' && dataSearch[key] > 0) {
            updateDataSearch.accept_class.pop()
          }
        }
        setDataSearch(updateDataSearch)
        break
      }
      case 'option': {
        if (key === 'stay_day' || key === 'category_name') {
          let currentData = dataSearch[key]
          const isValueChosen = currentData?.includes(String(newValue))
          const updatedData = isValueChosen
            ? handleRemoveChoseValueFromDataArray(currentData, String(newValue))
            : handleAddChoseValueToDataArray(currentData, String(newValue))
          setDataSearch((prevDataSearch) => ({ ...prevDataSearch, [key]: updatedData }))
          return
        }

        if (key === 'plan_month') {
          let currentPlanMonth = dataSearch[key]
          const isValueChosen = currentPlanMonth?.includes(String(newValue))
          const isValueChosenOverThree = currentPlanMonth?.length >= 3

          let updatedPlanMonth = []
          if (isValueChosen) {
            updatedPlanMonth = handleRemoveChoseValueFromDataArray(currentPlanMonth, String(newValue))
          } else if (isValueChosenOverThree) {
            return
          } else {
            updatedPlanMonth = handleAddChoseValueToDataArray(currentPlanMonth, String(newValue))
          }
          setDataSearch((prevDataSearch) => ({ ...prevDataSearch, [key]: updatedPlanMonth }))
          return
        }
        break
      }
      case 'text': {
        const isValueChosen = dataSearch[key] === value
        newValue = isValueChosen ? '' : value
        break
      }
    }

    setDataSearch((prevDataSearch) => ({ ...prevDataSearch, [key]: newValue }))
  }

  const handleClearForm = () => {
    setDataSearch(DATA_SEARCH_INIT)
  }

  const handleCheckIsDataSearchEmpty = () => {
    const isDataSearchEmpty = JSON.stringify(dataSearch) === JSON.stringify(DATA_SEARCH_INIT)
    return isDataSearchEmpty
  }

  const handleSubmit = () => {
    const isResetDataSearch = handleCheckIsDataSearchEmpty()
    if (isResetDataSearch) {
      handleClearTopUrl()
    }
    onChangeDataSearch(dataSearch)
  }

  const handleChangeChildrentList = (itemId, value) => {
    const currentChildrenList = [...dataSearch.accept_class]
    const updateChildrenList = currentChildrenList?.map((item) => {
      if (item.id === itemId) return { ...item, value: value }
      return item
    })

    setDataSearch((prevDataSearch) => ({ ...prevDataSearch, accept_class: updateChildrenList }))
  }

  const renderChildrenList = () => {
    return (
      <div className="flex flex-wrap justify-start gap-15px">
        {dataSearch?.accept_class?.map((item) => (
          <div key={item.id} className="flex items-center gap-2">
            <span className="text-sm">{t('reservation.children_info.count', { count: item.id })}</span>
            <select
              value={item.value}
              onChange={(e) => handleChangeChildrentList(item.id, e.target.value)}
              className="rounded-lg text-sm children-option border-gray-300 text-ellipsis"
              style={{
                width: isSmallMobileScreen ? '95px' : '110px',
              }}
            >
              {ACCEPT_CLASS_OPTIONS.map((age) => (
                <option key={age.id} value={age.value} className="text-sm">
                  {age.name}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    )
  }

  const handleRenderPlanMonth = (year: number) => {
    const isCurrentYear = year === new Date().getFullYear()

    return (
      <>
        <div className="text-left text-black-900 mt-3 font-size-14">{year}</div>
        <div className="flex flex-wrap w-full gap-20px mt-3 mx-w-330">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((item) => {
            const currentMonth = new Date().getMonth() + 1
            const value = `${year}/${item > 9 ? item : `0${item}`}`
            const isActive = dataSearch.plan_month?.includes(value)
            if (isCurrentYear && currentMonth > 4 && item <= 4) return null
            if (isCurrentYear && currentMonth > 8 && item <= 8) return null
            return (
              <button
                key={item}
                disabled={item < currentMonth && isCurrentYear}
                onClick={() => handleChangeDataSearch('plan_month', `${value}`, 'option')}
                className="all-unset padding-5-15 border border-black rounded-lg text-sm"
                style={{
                  color: isActive ? 'white' : '#787878',
                  backgroundColor: isActive ? 'black' : 'white',
                  textDecoration: item < currentMonth && isCurrentYear ? 'line-through' : 'none',
                }}
              >
                {t('month.abbr_month_names.' + item)}
              </button>
            )
          })}
        </div>
      </>
    )
  }

  if (!open) return

  return (
    <>
      <div
        className="modal-search-backdrop fixed top-0 left-0 right-0 z-50 w-full overflow-x-hidden overflow-y-auto md:inset-0 max-h-full h-full justify-center items-center flex"
        role="dialog"
      >
        <div
          className="relative w-full overflow-y-auto"
          style={{
            width: isMobileScreen ? '100%' : '420px',
            display: isMobileScreen ? 'block' : 'flex',
            alignItems: 'normal',
            height: isMobileScreen ? '100%' : isTabletScreen ? '77%' : '85%',
            animation: 'slide-down 0.4s ease-in-out forwards',
            borderRadius: isMobileScreen ? '0px' : '22px',
          }}
        >
          <div
            className="shadow-sm dark:bg-gray-700"
            ref={modalSearchRef}
            style={{
              height: '100%',
              width: '100%',
              backgroundColor: isMobileScreen ? 'white' : 'transparent',
              borderRadius: isMobileScreen ? '0px' : '22px',
              marginLeft: isMobileScreen ? '0px' : 'auto',
              marginRight: isMobileScreen ? '0px' : 'auto',
            }}
          >
            <div
              className="flex bg-white items-center justify-between p-4 rounded-t"
              style={{
                borderRadius: isMobileScreen ? '0' : '16px 16px 0px 0px',
                height: isSmallMobileScreen ? '12%' : '8%',
              }}
            >
              <h3 className="mt-1 font-semibold dark:text-white text-black font-size-16px">{t('top.modal_search.header')}</h3>
              <button
                className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
                type="button"
                onClick={onClose}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>
            <div
              ref={modalSearchContentRef}
              className="space-y-6 md:mb-0 bg-white"
              style={{
                height: isSmallMobileScreen
                  ? '80dvh'
                  : isMobileScreen
                    ? '83dvh'
                    : isContentOverHeight
                      ? '80%'
                      : isTabletScreen
                        ? '75%'
                        : 'unset',
                maxHeight: isSmallMobileScreen ? '76%' : '80%',
                overflow: 'auto',
                padding: isSmallMobileScreen ? '10px' : '1rem 1.5rem 0.5rem 1.5rem',
              }}
            >
              <div className="w-full grid grid-cols-12">
                <div className="col-span-8">
                  <div className="title font-bold mb-4 text-left text-black-700">
                    {t('plan_detail.reservation.modal.people.title')}
                  </div>
                </div>
                <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
                  <div className="title font-bold">{t('plan_detail.reservation.modal.people.adult')}</div>
                </div>
                <div
                  className={`${
                    isSmallMobileScreen ? 'col-start-7 col-span-6' : 'col-start-9 col-span-4'
                  } flex items-cennter gap-2 h-8 ml-auto`}
                >
                  <button
                    onClick={() => handleChangeDataSearch('maximum_number_of_adults', '', 'down')}
                    className="all-unset div rounded-full cursor-pointer select-none rounded-bl-full"
                  >
                    <IconMinus />
                  </button>
                  <div className="text-center mt-1 w-6">{dataSearch.maximum_number_of_adults}</div>
                  <button
                    className="all-unset rounded-full cursor-pointer rounded-bl-full md:ml-0"
                    onClick={() => handleChangeDataSearch('maximum_number_of_adults', '', 'up')}
                  >
                    <IconPlus />
                  </button>
                </div>
              </div>
              <div className="w-full grid grid-cols-12">
                <div className={`${isSmallMobileScreen ? 'col-start-1 col-span-6' : 'col-start-1 col-span-8'} text-left`}>
                  <div className="title font-bold">{t('plan_detail.reservation.modal.people.child')}</div>
                </div>
                <div
                  className={`${
                    isSmallMobileScreen ? 'col-start-7 col-span-6' : 'col-start-9 col-span-4'
                  } flex items-cennter gap-2 h-8 ml-auto`}
                >
                  <button
                    onClick={() => handleChangeDataSearch('maximum_number_of_children', '', 'down')}
                    className="div rounded-full cursor-pointer select-none"
                  >
                    <IconMinus />
                  </button>
                  <div className="mt-1 w-6 text-center">{dataSearch.maximum_number_of_children}</div>
                  <button
                    onClick={() => handleChangeDataSearch('maximum_number_of_children', '', 'up')}
                    className="all-unset cursor-pointer"
                  >
                    <IconPlus />
                  </button>
                </div>
              </div>
              {renderChildrenList()}
              <div className="w-full grid grid-cols-12 mb-4 mt-5">
                <div className="col-span-8 text-left">
                  <div className="title font-semibold text-gray-500">{t('top.modal_search.local_transportation.title')}</div>
                </div>
                <div className="col-span-12 flex items-center mt-2 gap-2">
                  <input
                    id="no_car"
                    type="checkbox"
                    checked={!!dataSearch.no_car}
                    className="text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-black-500 dark:focus:ring-black-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    onChange={() => handleChangeDataSearch('no_car', t('search.sections.popular.tags.no_car.value'), 'text')}
                  />
                  <label htmlFor="no_car" className="text-black-900 text-sm">
                    {t('top.modal_search.local_transportation.option_1')}
                  </label>
                </div>
              </div>

              <div className="w-full grid grid-cols-12 mb-4 mt-5">
                <div className="col-span-8 text-left">
                  <div className="title font-semibold text-gray-500">{t('top.modal_search.stay_day.title')}</div>
                </div>
                <div className="col-span-12">
                  <div className="title text-left text-gray-500 text-sm mt-2">{t('top.modal_search.stay_day.subtitle')}</div>
                  <div className="flex flex-wrap w-full gap-15px mt-3">
                    {STAY_DAY_OPTIONS.map((item) => {
                      const isActive = dataSearch.stay_day.includes(item.value)
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleChangeDataSearch('stay_day', item.value, 'option')}
                          className="all-unset border border-black padding-5-15 rounded-lg text-sm"
                          style={{
                            color: isActive ? 'white' : '#787878',
                            backgroundColor: isActive ? 'black' : 'white',
                          }}
                        >
                          {item.name}
                        </button>
                      )
                    })}
                  </div>
                  <div className="title text-left text-gray-900 text-sm mt-5">{t('top.modal_search.plan_month.title')}</div>
                  {handleRenderPlanMonth(currentYear)}
                  {handleRenderPlanMonth(currentYear + 1)}
                </div>
              </div>
              <div className="w-full grid grid-cols-12 mb-4 mt-5">
                <div className="col-span-12 text-left">
                  <div className="title font-semibold text-gray-500">{t('top.modal_search.region.title')}</div>
                </div>
                <div className="col-span-12">
                  <div className="flex flex-wrap w-full gap-15px mt-3">
                    {REGION_OPTIONS.map((item) => {
                      const isActive = dataSearch.category_name.includes(item.value)
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleChangeDataSearch('category_name', item.value, 'option')}
                          className="all-unset border border-black padding-5-15 rounded-lg text-sm"
                          style={{
                            color: isActive ? 'white' : '#787878',
                            backgroundColor: isActive ? 'black' : 'white',
                          }}
                        >
                          {item.name}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
              <div className="w-full grid grid-cols-12 mb-4 mt-5">
                <div className="mt-4 col-span-12">
                  <SuggestionTextSearch textSearch={dataSearch.text_search} onChange={handleChangeDataSearch} />
                </div>
              </div>
            </div>
            <div
              className="flex w-full items-center p-4 space-x-2 rounded-b bg-white"
              style={{
                borderRadius: isMobileScreen ? '0px' : '0px 0px 16px 16px',
                position: isMobileScreen || isContentOverHeight ? 'fixed' : 'unset',
                height: isMobileScreen || isTabletScreen ? '12%' : '10%',
              }}
            >
              <button
                className="w-1/2 md:w-1/2 text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-hidden focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-500 dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-gray-600 py-2.5"
                style={{
                  padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
                }}
                onClick={handleClearForm}
                type="button"
              >
                {t('top.filtering.button.clear')}
              </button>

              <button
                className="w-1/2 md:w-1/2 background-green-primary text-white focus:ring-4 focus:outline-hidden focus:ring-blue-300 font-medium rounded-lg text-sm px-5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 py-2.5"
                onClick={handleSubmit}
                style={{
                  padding: isSmallMobileScreen ? '0.4rem 1rem' : '0.7rem 1rem',
                }}
                type="button"
              >
                {t('top.filtering.button.apply')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
